import { Component, Emit, Ref, Vue } from 'vue-property-decorator'
import BillingModalInvoicesCrudComponent from '@/modules/billing-module/components/modal/invoices/crud/billing-modal-invoices-crud.vue'
import BillingModalInvoicesCrud from '@/modules/billing-module/components/modal/invoices/crud/billing-modal-invoices-crud'
import BillingModalInvoicePaymentComponent from '@/modules/billing-module/components/modal/invoice-payment/billing-modal-invoice-payment.vue'
import BillingModalInvoicePayment from '@/modules/billing-module/components/modal/invoice-payment/billing-modal-invoice-payment'
import BillingModalInvoiceReceiptComponent from '@/modules/billing-module/components/modal/invoice-receipt/billing-modal-invoice-receipt.vue'
import BillingModalInvoiceReceipt from '@/modules/billing-module/components/modal/invoice-receipt/billing-modal-invoice-receipt'
import { InvoiceStatus, InvoiceResponse } from '@/api/services/billing/common-invoice/billing-common-invoice.dto'
import { BillingStoreHelper } from '@/modules/billing-module/store/billing-store.helper'
import { MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { StrictNullable } from '@/shared/types/app.types'
import { billingCommonInvoiceService } from '@/api/services/billing/common-invoice/billing-common-invoice.service'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { FileUtils } from '@/shared/util/file-utils'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component({
    components: {
        BillingModalInvoicesCrudComponent,
        BillingModalInvoicePaymentComponent,
        BillingModalInvoiceReceiptComponent
    }
})
export default class BillingGridActionsInvoices extends Vue {
    @Ref() readonly invoiceModal!: BillingModalInvoicesCrud
    @Ref() readonly invoicePaymentModal!: BillingModalInvoicePayment
    @Ref() readonly invoiceReceiptModal!: BillingModalInvoiceReceipt

    @Emit()
    refresh(): void {
        // emits an event
    }

    private static get _selectedRows(): InvoiceResponse[] {
        return BillingStoreHelper.masterSelectedRows as InvoiceResponse[]
    }
    private static get _selectedRow(): StrictNullable<InvoiceResponse> {
        return BillingGridActionsInvoices._selectedRows.length === 1
            ? BillingGridActionsInvoices._selectedRows[0]
            : null
    }

    get canUpdateInvoice(): boolean {
        return BillingGridActionsInvoices._selectedRows.length === 1 &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICES)
    }
    get canCancelInvoice(): boolean {
        return BillingGridActionsInvoices._selectedRow?.status.code === InvoiceStatus.GENERATED &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICES)
    }
    get canOpenInvoiceReport(): boolean {
        return BillingGridActionsInvoices._selectedRows.length === 1
    }
    get canCreateInvoicePayment(): boolean {
        return BillingGridActionsInvoices._selectedRows.length === 1 &&
            BillingGridActionsInvoices._selectedRow?.status.code === InvoiceStatus.VERIFIED &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICE_PAYMENTS)
    }
    get canCreateInvoiceReceipt(): boolean {
        return BillingGridActionsInvoices._selectedRows.length === 1 &&
            BillingGridActionsInvoices._selectedRow?.status.code === InvoiceStatus.VERIFIED
    }

    /// SERVICE CALLS ///

    private async _cancelInvoice(invoiceId: number): Promise<boolean> {
        const response =
            await PromiseUtils.toCallSecure(
                billingCommonInvoiceService.cancel(store.state.labId, invoiceId))
        if (response !== null) {
            ToastUtils.showToastSuccess(this.$tc('accounting.invoice.cancelled'))
        }
        return response !== null
    }

    private _downloadInvoiceReport(invoiceId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingCommonInvoiceService.getReport(store.getters.labId, invoiceId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// EVENT HANDLERS ///

    onUpdateInvoice(): void {
        if (BillingGridActionsInvoices._selectedRow) {
            this.invoiceModal.show({
                invoice: BillingGridActionsInvoices._selectedRow
            })
        }
    }

    async onCancelInvoice(): Promise<void> {
        if (BillingGridActionsInvoices._selectedRow) {
            const { id, appliedBillableConceptsCount } = BillingGridActionsInvoices._selectedRow
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptsCount,
                async () => {
                    if (await this._cancelInvoice(id)) {
                        this.refresh()
                    }
                }
            )
        }
    }

    async onOpenInvoiceReport(): Promise<void> {
        if (BillingGridActionsInvoices._selectedRow) {
            const blob = await this._downloadInvoiceReport(BillingGridActionsInvoices._selectedRow.id)
            if (blob !== null) {
                FileUtils.openFile({ blob })
            }
        }
    }

    onCreateInvoicePayment(): void {
        if (BillingGridActionsInvoices._selectedRow) {
            this.invoicePaymentModal.show({
                invoice: BillingGridActionsInvoices._selectedRow
            })
        }
    }

    onCreateInvoiceReceipt(): void {
        if (
            BillingGridActionsInvoices._selectedRow &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICE_RECEIPTS)
        ) {
            this.invoiceReceiptModal.show({
                invoice: BillingGridActionsInvoices._selectedRow
            })
        }
    }

    ///

    getContextMenuItems(): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (this.canUpdateInvoice) {
            menuItems.push({
                name: this.$tc('accounting.action.updateInvoice'),
                action: this.onUpdateInvoice
            })
        }
        if (this.canCancelInvoice) {
            menuItems.push({
                name: this.$tc('accounting.action.cancelInvoice'),
                action: this.onCancelInvoice
            })
        }
        if (this.canOpenInvoiceReport) {
            menuItems.push({
                name: this.$tc('global.action.openReport'),
                action: this.onOpenInvoiceReport
            })
        }
        if (this.canCreateInvoicePayment) {
            menuItems.push({
                name: this.$tc('accounting.action.generateInvoicePayment'),
                action: this.onCreateInvoicePayment
            })
        }
        if (this.canCreateInvoiceReceipt) {
            menuItems.push({
                name: this.$tc('accounting.action.generateInvoiceReceipt'),
                action: this.onCreateInvoiceReceipt
            })
        }
        return menuItems
    }
}
